import { reactivateUser } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { UserListItem } from '@jq4race/shared';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@jq4race/ui';
import { useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

interface ReactivateDialogProps {
  user: UserListItem | null;
  onClose: () => void;
  onReactivated: (user: UserListItem) => void;
}

// QA R2-02 — org-scoped counterpart of DeactivateDialog. A deactivated user
// cannot be re-invited (the e-mail stays registered), so this is the only way
// back in.
export function ReactivateDialog({ user, onClose, onReactivated }: ReactivateDialogProps) {
  const { t } = useTranslation('users');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setSubmitting(false);
      setError(null);
    }
  }, [user]);

  async function confirm() {
    if (!user) return;
    setSubmitting(true);
    setError(null);
    try {
      await reactivateUser(user.id);
      onReactivated(user);
      onClose();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('reactivateDialog.error') }));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={!!user} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('reactivateDialog.title')}</DialogTitle>
          <DialogDescription>{t('reactivateDialog.description')}</DialogDescription>
        </DialogHeader>

        <div className="mt-6 rounded-sm border border-jq-sand/70 bg-jq-surface-2/50 px-4 py-3">
          <p className="text-[12px] uppercase tracking-widest text-jq-graphite">
            {t('reactivateDialog.affectedAccount')}
          </p>
          <p className="mt-1 break-all font-mono text-[13px] text-jq-charcoal">{user?.email}</p>
        </div>

        {error && (
          <p
            role="alert"
            className="mt-4 rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-3 py-2 text-[13px] text-jq-danger"
          >
            {error}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('reactivateDialog.cancel')}
          </Button>
          <Button type="button" size="sm" onClick={confirm} disabled={submitting}>
            {submitting ? t('reactivateDialog.confirming') : t('reactivateDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
